/**
 * Admin → Testimonials panel.
 * Lists pending / approved / rejected submissions and lets the admin
 * approve, reject, or delete each.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Trash2, RefreshCw, Quote, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

const STATUSES = [
  { id: 'pending',  label: 'Pending',  tone: 'amber' },
  { id: 'approved', label: 'Approved', tone: 'emerald' },
  { id: 'rejected', label: 'Rejected', tone: 'rose' },
];

const TONE = {
  amber:   'bg-amber-100 text-amber-800 border-amber-300',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rose:    'bg-rose-100 text-rose-800 border-rose-300',
};

export default function TestimonialsAdminPanel({ adminToken }) {
  const [filter, setFilter] = useState('pending');
  const [data, setData] = useState({ counts: {}, testimonials: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const url = `${API}/api/admin/testimonials${filter ? `?status=${filter}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${adminToken}` },
        cache: 'no-store',
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      setData(await res.json());
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [adminToken, filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    if (action === 'delete' && !window.confirm('Permanently delete this testimonial?')) return;
    setActingId(id); setErr('');
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const path =
        action === 'delete' ? `/api/admin/testimonials/${id}` :
        action === 'approve' ? `/api/admin/testimonials/${id}/approve` :
                               `/api/admin/testimonials/${id}/reject`;
      const res = await fetch(`${API}${path}`, {
        method,
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      load();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setActingId(null);
    }
  };

  const counts = data.counts || {};
  const rows = data.testimonials || [];

  return (
    <div data-testid="admin-testimonials-panel" className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-5 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="bg-purple-600 text-white rounded-lg p-3"><Quote className="w-6 h-6" /></div>
          <div>
            <h2 className="text-xl font-extrabold text-purple-900">Testimonials</h2>
            <p className="text-sm text-purple-800 mt-1 max-w-2xl">
              Submissions from <code className="bg-white px-1.5 py-0.5 rounded text-[11px] border border-purple-200">/founder/share</code>.
              Approve them to publish at <code className="bg-white px-1.5 py-0.5 rounded text-[11px] border border-purple-200">/api/public/testimonials</code>
              {' '}(used by the OnSpace landing-page widget).
            </p>
          </div>
        </div>
        <button
          data-testid="testimonials-refresh"
          onClick={load}
          className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-bold"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Status tabs */}
      <div className="bg-white border border-slate-200 rounded-xl p-2 flex flex-wrap gap-1">
        {STATUSES.map((s) => {
          const on = filter === s.id;
          const cnt = counts[s.id] ?? 0;
          return (
            <button
              key={s.id}
              data-testid={`testimonials-tab-${s.id}`}
              onClick={() => setFilter(s.id)}
              className={`px-3 py-2 text-sm font-bold rounded-lg flex items-center gap-2 transition-colors ${
                on ? 'bg-purple-600 text-white' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {s.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${on ? 'bg-white text-purple-700' : `border ${TONE[s.tone]}`}`}>
                {cnt}
              </span>
            </button>
          );
        })}
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-3 text-sm text-rose-800">{err}</div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No {filter} testimonials yet.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((t) => (
              <div
                key={t.id}
                data-testid={`testimonial-row-${t.id}`}
                className="p-4 sm:p-5 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-slate-900 italic leading-relaxed">"{t.quote}"</p>
                    <p className="text-xs text-slate-600 mt-2">
                      <strong className="text-slate-900">{t.name}</strong>
                      {t.title && <span>, {t.title}</span>}
                      {t.practiceName && <span> · {t.practiceName}</span>}
                      {(t.city || t.state) && (
                        <span> · {[t.city, t.state].filter(Boolean).join(', ')}</span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Submitted {fmt(t.createdAt)}
                      {t.email && <span> · {t.email}</span>}
                      {t.practiceId && <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold">in-app</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.status !== 'approved' && (
                      <button
                        data-testid={`testimonial-approve-${t.id}`}
                        disabled={actingId === t.id}
                        onClick={() => act(t.id, 'approve')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </button>
                    )}
                    {t.status !== 'rejected' && (
                      <button
                        data-testid={`testimonial-reject-${t.id}`}
                        disabled={actingId === t.id}
                        onClick={() => act(t.id, 'reject')}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    )}
                    <button
                      data-testid={`testimonial-delete-${t.id}`}
                      disabled={actingId === t.id}
                      onClick={() => act(t.id, 'delete')}
                      className="bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
